import { type Listing, type TrackerData, trackEvent, trackPageView } from '@finn-no/pulse-sdk';
import { safeParseISO } from 'shared/util/datetime-fns';
import type { FhhState, Filters, ObjectData, ResultData } from '@/domain';

export type ObjectTrackingStatus = 'inactive' | 'published' | 'draft';

const extractNumberOfBedrooms = (objectData: ObjectData) =>
    // blank string, NaN or 0 should return null
    Number(objectData?.noOfBedrooms) || undefined;
const calculateAverageWeeklyPrice = (objectData: ObjectData) => {
    const currency = objectData?.pricing?.currency;
    const from = objectData?.pricing?.week?.from;
    const to = objectData?.pricing?.week?.to;

    if (currency === 'NOK' && Number.isInteger(from) && Number.isInteger(to)) {
        // Should only track whole numbers
        return Math.round(((from as number) + (to as number)) / 2);
    }
    return undefined;
};

const trackCommonClick = ({
    intent,
    name,
    objectName,
    objectData,
    targetData,
    fromDate,
    toDate,
    numberOfAdults,
    numberOfChildren,
    numberOfInfants,
}: {
    intent: TrackerData['intent'];
    name: string;
    objectName?: string;
    objectData: ObjectData;
    targetData?: TrackerData['target'];
    fromDate?: string;
    toDate?: string;
    numberOfAdults?: number;
    numberOfChildren?: number;
    numberOfInfants?: number;
}) =>
    trackEvent({
        type: 'Click',
        intent,
        name,
        object: {
            id: name.replaceAll(' ', '_').toLowerCase(),
            type: 'UIElement',
            elementType: 'Button',
            name: objectName ?? undefined,
            hasExtendedProfile: false,
            items: [
                {
                    type: 'ClassifiedAd',
                    id: objectData?.adId?.toString() ?? '',
                    contentId: objectData?.adId?.toString() ?? '',
                    name: objectData?.heading == null ? '' : objectData?.heading,
                    category: 'travel > holiday rental',
                },
                {
                    type: 'Travel',
                    id: 'finntravel',
                    travelType: 'holiday_rental',
                    // Due to legacy definitions in Pulse, `destination` is used instead of `arrivalCountry`
                    destination: objectData?.location?.country ?? undefined,
                    arrivalCity: objectData?.location?.locality ?? undefined,
                    // TODO skj better to get a regular date as input instead of a string
                    departureDate: safeParseISO(fromDate)?.toISOString() ?? undefined,
                    returnDate: safeParseISO(toDate)?.toISOString() ?? undefined,
                    numberOfAdults,
                    numberOfChildren,
                    numberOfInfants,
                    numberOfRooms: extractNumberOfBedrooms(objectData),
                    totalPrice: calculateAverageWeeklyPrice(objectData),
                    provider: objectData?.organisation?.name ?? undefined,
                },
            ],
        },
        target: targetData,
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });

export const trackBookingPageView = (adId: string) => {
    // Pulse tracker id: FHH_pv04 (see tracking section in README.md)
    trackPageView({
        object: {
            category: 'travel > holiday rental',
            type: 'Page',
            pageType: 'tool',
            items: [
                {
                    id: adId,
                    type: 'ClassifiedAd',
                    contentId: adId,
                    name: 'Booking page',
                    category: 'travel > holiday rental',
                },
            ],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

export const trackObjectPageView = (itemId: number, status: ObjectTrackingStatus, brandName?: string) => {
    // Pulse tracker id: FHH_pv02 (see tracking section in README.md)
    trackPageView({
        object: {
            id: itemId.toString(),
            name: 'FHH object page',
            type: 'ClassifiedAd',
            category: 'travel > holiday rental',
            contentId: itemId.toString(),
            status: status,
            externalPartners: [
                {
                    brandName,
                },
            ],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

export const trackAdInsertionInfoPageView = (itemId?: string) => {
    // Pulse tracker id: IF_pv22
    trackPageView({
        name: 'Ad insertion - travel-holiday-rental',
        object: {
            name: itemId ? 'Annonseredigering' : 'Annonseinnlegging',
            category: 'travel > holiday rental',
            type: 'Form',
            stepName: 'Info about FHH',
            action: itemId ? 'Edit ad' : 'Create ad',
            items: itemId
                ? [
                      {
                          type: 'ClassifiedAd',
                          id: itemId,
                          contentId: itemId,
                          name: 'n/a',
                          category: 'n/a',
                      },
                  ]
                : [],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

export const trackAdInsertionDetails = (itemId: string, kycNeeded: boolean, editing: boolean) => {
    // Pulse tracker id: IF_pv24
    trackPageView({
        name: 'Ad insertion - travel-holiday-rental',
        object: {
            name: editing ? 'Annonseredigering' : 'Annonseinnlegging',
            category: 'travel > holiday rental',
            type: 'Form',
            stepName: 'Pricing and specification of rental object',
            action: kycNeeded ? 'bankid not verified' : 'bankid verified',
            items: [
                {
                    type: 'ClassifiedAd',
                    id: itemId,
                    contentId: itemId,
                    name: 'n/a',
                    category: 'n/a',
                },
            ],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

export const trackAdInsertionPersonalInformation = (itemId: string, editing: boolean) => {
    // Pulse tracker id: IF_pv25
    trackPageView({
        name: 'Ad insertion - travel-holiday-rental',
        object: {
            name: editing ? 'Annonseredigering' : 'Annonseinnlegging',
            category: 'travel > holiday rental',
            type: 'Form',
            stepName: 'Summary - personal information and contact details',
            action: editing ? 'Edit ad' : 'Create ad',
            items: [
                {
                    type: 'ClassifiedAd',
                    id: itemId,
                    contentId: itemId,
                    name: 'n/a',
                    category: 'n/a',
                },
            ],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

export const trackOutPageView = () => {
    // Pulse tracker id: FHH_pv09
    trackPageView({
        object: {
            category: 'travel > holiday rental',
            type: 'Page',
            pageType: 'redirect',
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

export const trackResultPage = (layout: Listing['layout'], props: { data: ResultData; filters: Filters }) => {
    const { data, filters } = props;

    const extractSelectionFilterList = (name: string, emptyOrStringOrList: string | string[] | undefined) => {
        if (typeof emptyOrStringOrList === 'string') {
            return [
                {
                    name,
                    value: emptyOrStringOrList,
                },
            ];
        }
        if (Array.isArray(emptyOrStringOrList)) {
            return emptyOrStringOrList.map((propertyType) => ({
                name,
                value: propertyType,
            }));
        }
        return [];
    };

    const landlordTypesFor = (isPrivate: boolean | string | undefined) => {
        if (isPrivate === true || isPrivate === 'true') {
            return [
                {
                    name: 'landlord_type',
                    value: 'private',
                },
            ];
        }
        if (isPrivate === false || isPrivate === 'false') {
            return [
                {
                    name: 'landlord_type',
                    value: 'business',
                },
            ];
        }
        return [
            {
                name: 'landlord_type',
                value: 'private',
            },
            {
                name: 'landlord_type',
                value: 'business',
            },
        ];
    };

    // TODO property type should be a name instead of a number
    const propertyTypes = extractSelectionFilterList('property_type', filters.property_types);
    const facilities = extractSelectionFilterList('facility', filters.facilities);
    const poisNearby = extractSelectionFilterList('poi_nearby', filters.pois_nearby);

    trackPageView({
        object: {
            category: 'travel > holiday rental',

            type: 'Listing',
            layout,
            filters: {
                // returns undefined instead of null or empty so Pulse removes it from the event it sends
                query: new URLSearchParams(window?.location.search).toString() || undefined,
            },
            sortingType: filters.sorting ?? 'best', // Default sorting type is 'Beste treff', i.e. 'best'
            numItems: data.stats?.numResults ?? -1,
            pageNumber: data.stats?.currentPage ?? -1,
            selectionFilters: [
                // TODO should any of these have a default value?
                {
                    name: 'booking_from',
                    value: filters.booking_from,
                },
                {
                    name: 'booking_to',
                    value: filters.booking_to,
                },
                {
                    name: 'no_of_beds_from',
                    value: filters.no_of_beds_from,
                },
                {
                    name: 'no_of_bedrooms_from',
                    value: filters.no_of_bedrooms_from,
                },
                {
                    name: 'is_transactional',
                    value: filters.is_transactional,
                },
                {
                    name: 'is_private',
                    value: filters.is_private,
                },
                {
                    name: 'price_from',
                    value: filters.price_from,
                },
                {
                    name: 'price_to',
                    value: filters.price_to,
                },
            ]
                .concat(propertyTypes)
                .concat(facilities)
                .concat(poisNearby)
                .concat(landlordTypesFor(filters.is_private))
                .filter(({ value }) => value !== undefined && value !== null)
                .map(({ name, value }) => ({ name, value: toValueString(value) })),
        },

        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

const toValueString = (value: string | boolean | number | undefined | null): string =>
    value === undefined || value === null ? '' : value.toString();

// Pulse tracker id: FHH_ev04 (see tracking section in README.md)
export const trackAddBookingToCalendar = (objectData: ObjectData, fromDate: string, toDate: string) =>
    trackEvent({
        type: 'Click',
        intent: 'Purchase',
        name: 'Add booking to calendar',
        object: {
            id: 'add_booking_to_calendar',
            type: 'UIElement',
            elementType: 'Button',
            hasExtendedProfile: false,
            items: [
                {
                    type: 'ClassifiedAd',
                    id: objectData?.adId?.toString() ?? '',
                    contentId: objectData?.adId?.toString() ?? '',
                    name: objectData?.heading == null ? '' : objectData?.heading,
                    category: 'travel > holiday rental',
                },
                {
                    type: 'Travel',
                    id: 'finntravel',
                    travelType: 'holiday_rental',
                    // Due to legacy definitions in Pulse, `destination` is used instead of `arrivalCountry`
                    destination: objectData?.location?.country ?? undefined,
                    arrivalCity: objectData?.location?.locality ?? undefined,
                    // TODO skj better to get a regular date as input instead of a string
                    departureDate: safeParseISO(fromDate)?.toISOString() ?? undefined,
                    returnDate: safeParseISO(toDate)?.toISOString() ?? undefined,
                    numberOfRooms: extractNumberOfBedrooms(objectData),
                    totalPrice: calculateAverageWeeklyPrice(objectData),
                    provider: objectData?.organisation?.name ?? undefined,
                },
            ],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });

// Pulse tracker id: FHH_ev05 (see tracking section in README.md)
export const trackConfirmOrder = (objectData: ObjectData, orderData: FhhState['orderData']) =>
    trackCommonClick({
        intent: 'Purchase',
        name: 'Book vacation home',
        objectData,
        fromDate: orderData?.fromDate ?? undefined,
        toDate: orderData?.toDate ?? undefined,
        numberOfAdults: orderData?.adults,
        numberOfChildren: orderData?.children,
        numberOfInfants: orderData?.infants,
        targetData: {
            type: 'Confirmation',
            id: objectData.owner.url || '',
            name: 'Go to confirmation page',
            referralCommission: true,
            provider: objectData.organisation?.name,
        },
    });

// Pulse tracker id: FHH_ev06 (see tracking section in README.md)
export const trackSendMessage = (data: { objectName: string; objectData: ObjectData; fromDate?: string; toDate?: string }) =>
    trackCommonClick({
        intent: 'Send',
        name: 'Send message',
        ...data,
    });

// Pulse tracker id: FHH_ev07 (see tracking section in README.md)
export const trackCheckPriceAndAvailability = (objectData: ObjectData) =>
    trackEvent({
        type: 'Click',
        intent: 'Open',
        name: 'Check price and availability',
        object: {
            id: 'check_price_and_availability',
            type: 'UIElement',
            elementType: 'Button',
            name: 'import offer',
            hasExtendedProfile: false,
            items: [
                {
                    type: 'ClassifiedAd',
                    id: objectData?.adId?.toString() ?? '',
                    contentId: objectData?.adId?.toString() ?? '',
                    name: objectData?.heading == null ? '' : objectData?.heading,
                    category: 'travel > holiday rental',
                },
                {
                    type: 'Travel',
                    id: 'finntravel',
                    travelType: 'holiday_rental',
                    // Due to legacy definitions in Pulse, `destination` is used instead of `arrivalCountry`
                    destination: objectData?.location?.country ?? undefined,
                    arrivalCity: objectData?.location?.locality ?? undefined,
                    numberOfRooms: extractNumberOfBedrooms(objectData),
                    totalPrice: calculateAverageWeeklyPrice(objectData),
                    provider: objectData?.organisation?.name ?? undefined,
                },
            ],
        },
        target: {
            id: objectData.owner.deeplink ?? 'unknown external URL',
            type: 'ExternalContent',
            name: 'Go to webpage',
            referralCommission: true,
            provider: objectData.organisation?.name ?? undefined,
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });

export const trackObjectPageLinkClick = (url: string, index: number) =>
    trackEvent({
        type: 'Click',
        intent: 'Open',
        name: 'Useful links',
        object: {
            id: `useful_link_${index}`,
            type: 'UIElement',
            elementType: 'Link',
            hasExtendedProfile: false,
        },
        target: {
            id: url,
            type: 'ExternalContent',
            name: 'Go to webpage',
            referralCommission: false,
            provider: 'TODO', // TODO what to put here? Name of ad owner?
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });

// Pulse tracker id: FHH_ev01
export const trackDigitalContractClick = (digitalContractUrl: string) =>
    trackEvent({
        type: 'Click',
        intent: 'Create',
        name: 'Create digital contract',
        object: {
            id: 'create_digital_contract',
            type: 'UIElement',
            elementType: 'Button',
            hasExtendedProfile: false,
        },
        target: {
            id: digitalContractUrl,
            type: 'ExternalContent',
            name: 'Go to webpage',
            referralCommission: false,
            provider: 'Swiftcourt',
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });

export const trackShareClick = (itemId: string, heading: string, name: string, type: string) => {
    trackEvent({
        type: 'Click',
        intent: 'Share',
        name: 'Share ad',
        object: {
            id: `share_ad_${name}`,
            type: 'UIElement',
            name: `${type} icon`,
            items: [
                {
                    type: 'ClassifiedAd',
                    id: itemId,
                    contentId: itemId,
                    name: heading,
                    category: 'travel > holiday rental',
                },
            ],
            hasExtendedProfile: false,
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

export const trackReportAdClick = (itemId: string, heading?: string) =>
    trackEvent({
        type: 'Click',
        intent: 'Report',
        name: 'Report ad',
        object: {
            type: 'UIElement',
            id: 'report-ad',
            name: 'link',
            items: [
                {
                    type: 'ClassifiedAd',
                    id: itemId,
                    contentId: itemId,
                    name: heading ?? '',
                    category: 'travel > holiday rental',
                },
            ],
            hasExtendedProfile: false,
        },
        target: {
            type: 'ClassifiedAd',
            id: itemId,
            contentId: itemId,
            name: 'Report Ad',
            category: 'travel > holiday rental',
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });

export const trackResultItem = (itemId: string, heading: string, rank: number, upsell: boolean, fusClassifiedProductId?: number) =>
    trackEvent({
        type: 'Click',
        intent: 'Open',
        name: 'Search result clicked',
        object: {
            id: itemId,
            type: 'ListingItem',
            name: 'Search result',
            rank: rank,
            isTopPremiumPosition: upsell,
            paidPromotionType: upsell ? 'fus' : undefined,
            paidPromotions: fusClassifiedProductId
                ? [
                      {
                          '@id': `urn:product:product-instance:${fusClassifiedProductId}`,
                      },
                  ]
                : undefined,
        },
        target: {
            id: itemId,
            type: 'ClassifiedAd',
            name: heading,
            contentId: itemId,
            category: 'travel > holiday rental',
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
